import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FiBook, FiUsers, FiAward, FiBookOpen, FiSearch, 
  FiChevronLeft, FiChevronRight, FiArrowRight, FiGlobe,
  FiFileText, FiLayers, FiDatabase, FiMonitor, FiDownload,
  FiPhone, FiMail, FiMapPin, FiClock, FiFacebook, FiInstagram, FiTwitter,
  FiMenu, FiX, FiExternalLink
} from 'react-icons/fi';
import './LandingPage.css';

const API_BASE = '/api';

const LandingPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [previewBooks, setPreviewBooks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const sliderRef = useRef(null);

  // Slider data
  const slides = [
    {
      id: 1,
      image: '/images/slider/slide1.svg',
      fallbackGradient: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
      title: 'Selamat Datang di',
      subtitle: 'E-Library STIKES Baramuli',
      description: 'Akses ribuan koleksi buku, jurnal ilmiah, dan referensi digital untuk mendukung kegiatan akademik Anda',
      cta: 'Mulai Jelajahi',
      ctaLink: '/login'
    },
    {
      id: 2,
      image: '/images/slider/slide2.svg',
      fallbackGradient: 'linear-gradient(135deg, #dc2626 0%, #ef4444 50%, #f87171 100%)',
      title: 'Koleksi Digital',
      subtitle: 'Perpustakaan Modern',
      description: 'Nikmati kemudahan membaca e-book, jurnal, dan artikel ilmiah kapan saja dan di mana saja',
      cta: 'Lihat Katalog',
      ctaLink: '/login'
    },
    {
      id: 3,
      image: '/images/slider/slide3.svg',
      fallbackGradient: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 50%, #dc2626 100%)',
      title: 'Akses Database',
      subtitle: 'Referensi Internasional',
      description: 'Terhubung dengan Google Scholar, EBSCO, Open Library, dan berbagai database jurnal internasional',
      cta: 'Akses Sekarang',
      ctaLink: '/login'
    }
  ];

  // Reference databases with official logos
  const referenceDatabases = [
    { 
      name: 'Google Scholar', 
      logo: 'https://scholar.google.com/intl/id/scholar/images/2x/scholar_logo_64dp.png',
      url: 'https://scholar.google.com',
      description: 'Database artikel ilmiah terbesar di dunia'
    },
    { 
      name: 'EBSCO', 
      logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/bf/EBSCO_Information_Services_20xx_logo.svg/1280px-EBSCO_Information_Services_20xx_logo.svg.png',
      url: 'https://www.ebsco.com',
      description: 'Database penelitian akademik premium'
    },
    { 
      name: 'Open Library', 
      logo: 'https://upload.wikimedia.org/wikipedia/commons/b/b6/Open_Library_tight_logo.svg',
      url: 'https://openlibrary.org',
      description: 'Perpustakaan digital open access'
    },
    { 
      name: 'Google Books', 
      logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/ba/Google_Books_logo_2015.svg/1200px-Google_Books_logo_2015.svg.png',
      url: 'https://books.google.com',
      description: 'Jutaan buku digital dari seluruh dunia'
    },
    { 
      name: 'DOAJ', 
      logo: 'https://upload.wikimedia.org/wikipedia/commons/d/d9/DOAJ_logo-colour.svg',
      url: 'https://doaj.org',
      description: 'Directory of Open Access Journals'
    },
    { 
      name: 'Garuda', 
      logo: 'https://jurnalfaktarbiyah.iainkediri.ac.id/public/site/images/puspoko_ponco/5-jsea-garuda1.png',
      url: 'https://garuda.kemdiktisaintek.go.id',
      description: 'Portal jurnal Indonesia'
    }
  ];

  // Digital library features
  const digitalFeatures = [
    {
      icon: FiMonitor,
      title: 'Akses 24/7',
      description: 'Baca buku dan jurnal kapan saja, di mana saja tanpa batasan waktu'
    },
    {
      icon: FiDownload,
      title: 'Download & Offline',
      description: 'Unduh koleksi untuk dibaca secara offline di perangkat Anda'
    },
    {
      icon: FiSearch,
      title: 'Pencarian Cepat',
      description: 'Temukan buku yang Anda cari dengan mudah menggunakan fitur pencarian'
    },
    {
      icon: FiLayers,
      title: 'Multi Format',
      description: 'Tersedia dalam berbagai format: PDF, EPUB, dan format digital lainnya'
    },
    {
      icon: FiDatabase,
      title: 'Koleksi Lengkap',
      description: 'Ribuan koleksi buku teks, referensi, jurnal, dan karya ilmiah'
    },
    {
      icon: FiGlobe,
      title: 'Akses Global',
      description: 'Terhubung dengan database jurnal internasional terkemuka'
    }
  ];

  // Fetch statistics
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_BASE}/public_stats.php`);
        const data = await response.json();
        if (data.success) {
          setStats(data.data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  // Fetch preview books for katalog section
  useEffect(() => {
    const fetchPreviewBooks = async () => {
      try {
        const response = await fetch(`${API_BASE}/public_books.php?limit=8&sort=popular`);
        const data = await response.json();
        if (data.success) {
          setPreviewBooks(data.data || []);
        }
      } catch (error) {
        console.error('Error fetching preview books:', error);
      }
    };
    fetchPreviewBooks();
  }, []);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/katalog-publik?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/katalog-publik');
    }
  };

  // Auto-play slider
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, slides.length]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, []);

  const prevSlide = () => {
    goToSlide(currentSlide === 0 ? slides.length - 1 : currentSlide - 1);
  };

  const nextSlide = () => {
    goToSlide((currentSlide + 1) % slides.length);
  };

  // Get category icon based on name
  const getCategoryIcon = (name) => {
    const lowerName = name?.toLowerCase() || '';
    if (lowerName.includes('kesehatan') || lowerName.includes('medis')) return '🏥';
    if (lowerName.includes('keperawatan') || lowerName.includes('ners')) return '👩‍⚕️';
    if (lowerName.includes('teknologi') || lowerName.includes('komputer')) return '💻';
    if (lowerName.includes('pendidikan') || lowerName.includes('pembelajaran')) return '📚';
    if (lowerName.includes('penelitian') || lowerName.includes('riset')) return '🔬';
    if (lowerName.includes('manajemen') || lowerName.includes('bisnis')) return '📊';
    if (lowerName.includes('psikologi')) return '🧠';
    if (lowerName.includes('gizi') || lowerName.includes('nutrisi')) return '🥗';
    if (lowerName.includes('farmasi') || lowerName.includes('obat')) return '💊';
    if (lowerName.includes('jurnal')) return '📰';
    return '📖';
  };

  // Get prodi color
  const getProdiColor = (index) => {
    const colors = [
      { bg: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', icon: '🎓' },
      { bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', icon: '⚕️' },
      { bg: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', icon: '🏥' },
      { bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', icon: '📋' },
      { bg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', icon: '👨‍🏫' }
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="landing">
      {/* Navigation */}
      <nav className="landing__nav">
        <div className="landing__nav-container">
          <div className="landing__logo">
            <img src="/images/logo.png" alt="STIKBAR" />
            <div className="landing__logo-text">
              <span className="landing__logo-title">E-Library</span>
              <span className="landing__logo-subtitle">STIKES Baramuli</span>
            </div>
          </div>
          
          {/* Mobile Menu Toggle */}
          <button 
            className="landing__mobile-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <FiX /> : <FiMenu />}
          </button>
          
          <div className={`landing__nav-links ${mobileMenuOpen ? 'landing__nav-links--open' : ''}`}>
            <a href="#beranda" onClick={() => setMobileMenuOpen(false)}>Beranda</a>
            <a href="#koleksi" onClick={() => setMobileMenuOpen(false)}>Koleksi</a>
            <a href="#anggota" onClick={() => setMobileMenuOpen(false)}>Anggota</a>
            <a href="#referensi" onClick={() => setMobileMenuOpen(false)}>Referensi</a>
            <a href="#digital" onClick={() => setMobileMenuOpen(false)}>Perpustakaan Digital</a>
            <button className="landing__login-btn landing__login-btn--mobile" onClick={() => navigate('/login')}>
              <FiUsers />
              <span>Masuk</span>
            </button>
          </div>
          <button className="landing__login-btn landing__login-btn--desktop" onClick={() => navigate('/login')}>
            <FiUsers />
            <span>Masuk</span>
          </button>
        </div>
      </nav>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div className="landing__mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Hero Slider */}
      <section id="beranda" className="landing__hero" ref={sliderRef}>
        <div className="landing__slider">
          {slides.map((slide, index) => (
            <div 
              key={slide.id}
              className={`landing__slide ${index === currentSlide ? 'active' : ''}`}
              style={{ 
                background: slide.fallbackGradient
              }}
            >
              <div className="landing__slide-overlay"></div>
              <div className="landing__slide-content">
                <span className="landing__slide-label">{slide.title}</span>
                <h1 className="landing__slide-title">{slide.subtitle}</h1>
                <p className="landing__slide-description">{slide.description}</p>
                <button 
                  className="landing__slide-cta"
                  onClick={() => navigate(slide.ctaLink)}
                >
                  {slide.cta}
                  <FiArrowRight />
                </button>
              </div>
            </div>
          ))}
          
          {/* Slider Controls */}
          <button className="landing__slider-btn landing__slider-btn--prev" onClick={prevSlide}>
            <FiChevronLeft />
          </button>
          <button className="landing__slider-btn landing__slider-btn--next" onClick={nextSlide}>
            <FiChevronRight />
          </button>
          
          {/* Slider Dots */}
          <div className="landing__slider-dots">
            {slides.map((_, index) => (
              <button 
                key={index}
                className={`landing__slider-dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Stats Overview - Below Slider */}
      <section className="landing__stats-section">
        <div className="landing__stats-container">
          <div className="landing__stat-card">
            <div className="landing__stat-icon landing__stat-icon--books">
              <FiBook />
            </div>
            <div className="landing__stat-info">
              <span className="landing__stat-number">{stats?.total_buku || '0'}+</span>
              <span className="landing__stat-label">Koleksi Buku</span>
            </div>
          </div>
          <div className="landing__stat-card">
            <div className="landing__stat-icon landing__stat-icon--users">
              <FiUsers />
            </div>
            <div className="landing__stat-info">
              <span className="landing__stat-number">{stats?.total_anggota || '0'}+</span>
              <span className="landing__stat-label">Anggota Aktif</span>
            </div>
          </div>
          <div className="landing__stat-card">
            <div className="landing__stat-icon landing__stat-icon--journals">
              <FiFileText />
            </div>
            <div className="landing__stat-info">
              <span className="landing__stat-number">{stats?.total_jurnal || '0'}+</span>
              <span className="landing__stat-label">Jurnal Ilmiah</span>
            </div>
          </div>
          <div className="landing__stat-card">
            <div className="landing__stat-icon landing__stat-icon--loans">
              <FiAward />
            </div>
            <div className="landing__stat-info">
              <span className="landing__stat-number">{stats?.total_peminjaman || '0'}+</span>
              <span className="landing__stat-label">Total Peminjaman</span>
            </div>
          </div>
        </div>
      </section>

      {/* Katalog Buku Section */}
      <section className="landing__section landing__katalog">
        <div className="landing__section-container">
          <div className="landing__section-header">
            <span className="landing__section-label">Temukan Buku</span>
            <h2 className="landing__section-title">Katalog Buku</h2>
            <p className="landing__section-description">
              Cari dan temukan buku yang Anda butuhkan dari koleksi perpustakaan STIKES Baramuli
            </p>
          </div>
          
          <div className="landing__katalog-content">
            {/* Search Box */}
            <form className="landing__katalog-search" onSubmit={handleSearch}>
              <div className="landing__katalog-search-box">
                <FiSearch className="landing__katalog-search-icon" />
                <input 
                  type="text" 
                  placeholder="Cari judul buku, penulis, atau ISBN..." 
                  className="landing__katalog-search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="landing__katalog-search-btn">
                  Cari Buku
                </button>
              </div>
            </form>

            {/* Preview Books Marquee */}
            {previewBooks.length > 0 && (
              <div className="landing__katalog-books-wrapper">
                <div className="landing__katalog-books-marquee">
                  {/* Duplicate books for seamless loop */}
                  {[...previewBooks, ...previewBooks].map((book, index) => (
                    <Link 
                      to={`/katalog-publik/${book.id_buku}`} 
                      key={`${book.id_buku}-${index}`}
                      className="landing__katalog-book"
                    >
                      <div className="landing__katalog-book-cover">
                        <img 
                          src={book.cover_image || '/images/cover-default.png'} 
                          alt={book.judul}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/images/cover-default.png';
                          }}
                        />
                        {book.tipe_buku === 'external' || book.link_external ? (
                          book.is_readable === 1 || book.is_readable === '1' ? (
                            <span className="landing__katalog-book-badge landing__katalog-book-badge--free">
                              <FiGlobe /> Gratis
                            </span>
                          ) : (
                            <span className="landing__katalog-book-badge landing__katalog-book-badge--external">
                              <FiExternalLink /> Online
                            </span>
                          )
                        ) : parseInt(book.stok_tersedia) > 0 ? (
                          <span className="landing__katalog-book-badge landing__katalog-book-badge--available">
                            Tersedia
                          </span>
                        ) : (
                          <span className="landing__katalog-book-badge landing__katalog-book-badge--borrowed">
                            Dipinjam
                          </span>
                        )}
                      </div>
                      <div className="landing__katalog-book-info">
                        <h4 className="landing__katalog-book-title">{book.judul}</h4>
                        <p className="landing__katalog-book-author">{book.penulis || 'Unknown'}</p>
                        <span className="landing__katalog-book-category">{book.nama_kategori || 'Umum'}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Features */}
            <div className="landing__katalog-features">
              <div className="landing__katalog-feature">
                <div className="landing__katalog-feature-icon">
                  <FiBook />
                </div>
                <h4>Buku Fisik</h4>
                <p>Koleksi buku cetak yang dapat dipinjam</p>
              </div>
              <div className="landing__katalog-feature">
                <div className="landing__katalog-feature-icon">
                  <FiMonitor />
                </div>
                <h4>E-Book</h4>
                <p>Buku digital yang bisa dibaca online</p>
              </div>
              <div className="landing__katalog-feature">
                <div className="landing__katalog-feature-icon">
                  <FiFileText />
                </div>
                <h4>Jurnal</h4>
                <p>Artikel ilmiah dan penelitian</p>
              </div>
              <div className="landing__katalog-feature">
                <div className="landing__katalog-feature-icon">
                  <FiGlobe />
                </div>
                <h4>Open Access</h4>
                <p>Sumber referensi terbuka gratis</p>
              </div>
            </div>
            
            <div className="landing__katalog-cta">
              <Link to="/katalog-publik" className="landing__katalog-btn">
                <FiSearch /> Jelajahi Katalog Lengkap
              </Link>
              <p className="landing__katalog-total">
                {stats?.total_buku || '0'} buku tersedia di perpustakaan
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Book Categories Section */}
      <section id="koleksi" className="landing__section landing__categories">
        <div className="landing__section-container">
          <div className="landing__section-header">
            <span className="landing__section-label">Koleksi Kami</span>
            <h2 className="landing__section-title">Buku Berdasarkan Kategori</h2>
            <p className="landing__section-description">
              Jelajahi berbagai kategori buku yang tersedia di perpustakaan STIKES Baramuli
            </p>
          </div>
          
          <div className="landing__category-marquee">
            <div className="landing__category-track">
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <div key={i} className="landing__category-card landing__category-card--skeleton">
                    <div className="skeleton-icon"></div>
                    <div className="skeleton-text"></div>
                    <div className="skeleton-number"></div>
                  </div>
                ))
              ) : (
                <>
                  {/* First set */}
                  {stats?.buku_per_kategori?.filter(kategori => kategori.jumlah > 0).map((kategori, index) => (
                    <div key={`first-${index}`} className="landing__category-card">
                      <div className="landing__category-icon">
                        {getCategoryIcon(kategori.nama_kategori)}
                      </div>
                      <h3 className="landing__category-name">{kategori.nama_kategori}</h3>
                      <div className="landing__category-count">
                        <span className="landing__category-number">{kategori.jumlah}</span>
                        <span className="landing__category-label">Buku</span>
                      </div>
                      <div className="landing__category-bar">
                        <div 
                          className="landing__category-progress" 
                          style={{ width: `${Math.min((kategori.jumlah / (stats.total_buku || 1)) * 100 * 3, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                  {/* Duplicate set for seamless loop */}
                  {stats?.buku_per_kategori?.filter(kategori => kategori.jumlah > 0).map((kategori, index) => (
                    <div key={`second-${index}`} className="landing__category-card">
                      <div className="landing__category-icon">
                        {getCategoryIcon(kategori.nama_kategori)}
                      </div>
                      <h3 className="landing__category-name">{kategori.nama_kategori}</h3>
                      <div className="landing__category-count">
                        <span className="landing__category-number">{kategori.jumlah}</span>
                        <span className="landing__category-label">Buku</span>
                      </div>
                      <div className="landing__category-bar">
                        <div 
                          className="landing__category-progress" 
                          style={{ width: `${Math.min((kategori.jumlah / (stats.total_buku || 1)) * 100 * 3, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Members Section */}
      <section id="anggota" className="landing__section landing__members">
        <div className="landing__section-container">
          <div className="landing__section-header">
            <span className="landing__section-label">Komunitas Kami</span>
            <h2 className="landing__section-title">Anggota Perpustakaan</h2>
            <p className="landing__section-description">
              Bergabunglah dengan komunitas akademik STIKES BARAMULI
            </p>
          </div>
          
          <div className="landing__members-grid">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <div key={i} className="landing__member-card landing__member-card--skeleton">
                  <div className="skeleton-icon"></div>
                  <div className="skeleton-text"></div>
                  <div className="skeleton-number"></div>
                </div>
              ))
            ) : (
              stats?.anggota_per_prodi?.map((prodi, index) => {
                const colorStyle = getProdiColor(index);
                return (
                  <div 
                    key={index} 
                    className="landing__member-card"
                    style={{ animationDelay: `${index * 0.15}s` }}
                  >
                    <div className="landing__member-header" style={{ background: colorStyle.bg }}>
                      <span className="landing__member-icon">{colorStyle.icon}</span>
                    </div>
                    <div className="landing__member-body">
                      <h3 className="landing__member-name">{prodi.nama}</h3>
                      <div className="landing__member-count">
                        <span className="landing__member-number">{prodi.jumlah}</span>
                        <span className="landing__member-label">Anggota</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* Reference Databases Section */}
      <section id="referensi" className="landing__section landing__references">
        <div className="landing__section-container">
          <div className="landing__section-header">
            <span className="landing__section-label">Akses Database</span>
            <h2 className="landing__section-title">Database Referensi</h2>
            <p className="landing__section-description">
              Terhubung dengan berbagai database jurnal dan referensi ilmiah internasional
            </p>
          </div>
          
          <div className="landing__reference-grid">
            {referenceDatabases.map((db, index) => (
              <a 
                key={index}
                href={db.url}
                target="_blank"
                rel="noopener noreferrer"
                className="landing__reference-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="landing__reference-logo">
                  <img 
                    src={db.logo} 
                    alt={db.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <span className="landing__reference-fallback" style={{ display: 'none' }}>
                    {db.name}
                  </span>
                </div>
                <h3 className="landing__reference-name">{db.name}</h3>
                <p className="landing__reference-desc">{db.description}</p>
                <span className="landing__reference-link">
                  Kunjungi <FiArrowRight />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Digital Library Section */}
      <section id="digital" className="landing__section landing__digital">
        <div className="landing__digital-bg">
          <div className="landing__digital-shape landing__digital-shape--1"></div>
          <div className="landing__digital-shape landing__digital-shape--2"></div>
        </div>
        <div className="landing__section-container">
          <div className="landing__section-header landing__section-header--light">
            <span className="landing__section-label">Layanan Unggulan</span>
            <h2 className="landing__section-title">Perpustakaan Digital</h2>
            <p className="landing__section-description">
              Nikmati kemudahan akses perpustakaan digital dengan berbagai fitur modern
            </p>
          </div>
          
          <div className="landing__digital-grid">
            {digitalFeatures.map((feature, index) => (
              <div 
                key={index}
                className="landing__digital-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="landing__digital-icon">
                  <feature.icon />
                </div>
                <h3 className="landing__digital-title">{feature.title}</h3>
                <p className="landing__digital-desc">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="landing__digital-cta">
            <h3>Siap untuk mulai?</h3>
            <p>Daftar sekarang dan dapatkan akses ke seluruh koleksi perpustakaan digital</p>
            <button onClick={() => navigate('/login')} className="landing__digital-btn">
              <FiBookOpen />
              <span>Akses Perpustakaan Digital</span>
            </button>
          </div>
        </div>
      </section>

      {/* Popular Books Section */}
      {stats?.buku_populer?.length > 0 && (
        <section className="landing__section landing__popular">
          <div className="landing__section-container">
            <div className="landing__section-header">
              <span className="landing__section-label">Paling Diminati</span>
              <h2 className="landing__section-title">Buku Populer</h2>
              <p className="landing__section-description">
                Koleksi buku yang paling banyak dipinjam oleh anggota perpustakaan
              </p>
            </div>
            
            <div className="landing__popular-marquee">
              <div className="landing__popular-track">
                {/* First set */}
                {stats.buku_populer.map((buku, index) => (
                  <div key={`first-${index}`} className="landing__popular-card">
                    <div className="landing__popular-cover">
                      <img 
                        src={buku.cover_image 
                          ? `/uploads/covers/${buku.cover_image}` 
                          : '/images/cover-default.png'
                        } 
                        alt={buku.judul}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/cover-default.png';
                        }}
                      />
                      <div className="landing__popular-badge">#{index + 1}</div>
                    </div>
                    <div className="landing__popular-info">
                      <h4 className="landing__popular-title">{buku.judul}</h4>
                      <p className="landing__popular-author">{buku.penulis}</p>
                      <span className="landing__popular-borrowed">{buku.total_pinjam}x dipinjam</span>
                    </div>
                  </div>
                ))}
                {/* Duplicate set for seamless loop */}
                {stats.buku_populer.map((buku, index) => (
                  <div key={`second-${index}`} className="landing__popular-card">
                    <div className="landing__popular-cover">
                      <img 
                        src={buku.cover_image 
                          ? `/uploads/covers/${buku.cover_image}` 
                          : '/images/cover-default.png'
                        } 
                        alt={buku.judul}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/cover-default.png';
                        }}
                      />
                      <div className="landing__popular-badge">#{index + 1}</div>
                    </div>
                    <div className="landing__popular-info">
                      <h4 className="landing__popular-title">{buku.judul}</h4>
                      <p className="landing__popular-author">{buku.penulis}</p>
                      <span className="landing__popular-borrowed">{buku.total_pinjam}x dipinjam</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="landing__footer">
        <div className="landing__footer-top">
          <div className="landing__footer-container">
            <div className="landing__footer-brand">
              <div className="landing__logo">
                <img src="/images/logo.png" alt="STIKBAR" />
                <div className="landing__logo-text">
                  <span className="landing__logo-title">E-Library</span>
                  <span className="landing__logo-subtitle">STIKES BARAMULI</span>
                </div>
              </div>
              <p className="landing__footer-desc">
                Sistem Informasi Perpustakaan Digital Sekolah Tinggi Ilmu Kesehatan Baramuli. 
                Menyediakan akses mudah ke berbagai koleksi buku, jurnal, dan referensi ilmiah.
              </p>
              <div className="landing__footer-social">
                <a href="#"><FiFacebook /></a>
                <a href="#"><FiInstagram /></a>
                <a href="#"><FiTwitter /></a>
              </div>
            </div>
            
            <div className="landing__footer-links">
              <h4>Quick Links</h4>
              <ul>
                <li><a href="#beranda">Beranda</a></li>
                <li><a href="#koleksi">Koleksi Buku</a></li>
                <li><a href="#anggota">Anggota</a></li>
                <li><a href="#referensi">Database Referensi</a></li>
                <li><a href="#digital">Perpustakaan Digital</a></li>
              </ul>
            </div>
            
            <div className="landing__footer-links">
              <h4>Layanan</h4>
              <ul>
                <li><a href="#">Peminjaman Buku</a></li>
                <li><a href="#">Pengembalian Buku</a></li>
                <li><a href="#">Akses E-Book</a></li>
                <li><a href="#">Akses Jurnal</a></li>
                <li><a href="#">Kartu Anggota</a></li>
              </ul>
            </div>
            
            <div className="landing__footer-contact">
              <h4>Kontak</h4>
              <div className="landing__footer-contact-item">
                <FiMapPin />
                <span>Jl. Poros Polman-Pinrang KM.03, Paleteang</span>
              </div>
              <div className="landing__footer-contact-item">
                <FiPhone />
                <span>0858-2487-1455</span>
              </div>
              <div className="landing__footer-contact-item">
                <FiMail />
                <span>perpustakaan@stikesbaramuli.ac.id</span>
              </div>
              <div className="landing__footer-contact-item">
                <FiClock />
                <span>Senin - Jumat: 08.00 - 16.00 WITA</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="landing__footer-bottom">
          <div className="landing__footer-container">
            <p>&copy; {new Date().getFullYear()} Sekolah Tinggi Ilmu Kesehatan Baramuli. All rights reserved.</p>
            <p>E-Library System v1.0</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
